import { cn } from '@quadralize/utils'
import { AnimatePresence, motion } from 'framer-motion'
import { X } from 'lucide-react'
import { useMemo } from 'react'
import { useMobile } from '../../../hooks/use-mobile'
import { Badge } from '../badge/badge'
import type { DataTableFilter } from './data-table-filters.types'

interface ActiveFilter {
  id: string
  label: string
  displayValue: string
  onRemove: () => void
}

function getActiveFilters(filters: DataTableFilter[]): ActiveFilter[] {
  return filters.reduce<ActiveFilter[]>((acc, filter) => {
    if (filter.type === 'date') {
      const defaultVal = filter.defaultValue ?? ''
      if (filter.value === defaultVal) return acc

      acc.push({
        id: filter.id,
        label: filter.label,
        displayValue: filter.value,
        onRemove: () => filter.onValueChange(defaultVal),
      })
      return acc
    }

    const defaultVal = filter.defaultValue ?? 'all'
    if (String(filter.value) === String(defaultVal)) return acc

    const selectedOption = filter.options.find((opt) => String(opt.value) === String(filter.value))

    acc.push({
      id: filter.id,
      label: filter.label,
      displayValue: selectedOption?.label ?? String(filter.value),
      onRemove: () => filter.onValueChange(defaultVal),
    })

    return acc
  }, [])
}

interface DataTableActiveFiltersProps {
  filters: DataTableFilter[]
  className?: string
}

export function DataTableActiveFilters({ filters, className }: DataTableActiveFiltersProps) {
  const isMobile = useMobile()
  const activeFilters = useMemo(() => getActiveFilters(filters), [filters])

  if (activeFilters.length === 0) return null

  return (
    <div
      className={cn(
        isMobile
          ? 'flex items-center gap-2 overflow-x-auto overflow-y-hidden pb-1'
          : 'flex flex-wrap items-center gap-2',
        className,
      )}
    >
      <AnimatePresence mode="popLayout">
        {activeFilters.map((filter) => (
          <motion.div
            key={filter.id}
            layout
            className={isMobile ? 'shrink-0' : undefined}
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            transition={{ duration: 0.2 }}
          >
            <Badge
              variant="outline"
              size={isMobile ? 'sm' : 'lg'}
              rounded="full"
              title={`${filter.label}: ${filter.displayValue}`}
            >
              {isMobile ? (
                <span className="inline-flex max-w-48 items-center truncate leading-none">
                  {filter.displayValue}
                </span>
              ) : (
                <>
                  <span className="text-text-secondary font-normal">{filter.label}:</span>{' '}
                  <span className="max-w-40 truncate">{filter.displayValue}</span>
                </>
              )}
              <button
                type="button"
                onClick={filter.onRemove}
                className="ml-1 inline-flex h-3.5 w-3.5 items-center justify-center rounded-sm opacity-70 transition-opacity hover:opacity-100 focus:outline-none focus:ring-1 focus:ring-focus-ring"
                aria-label={`Remover filtro ${filter.label}`}
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  )
}
